import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { logger } from "@amzn/afxtaco-utilities";
import { useAuthentication } from "../auth/hooks/useAuthentication";
import { fetchFeatureFlags } from "../utils/FeatureFlagDetection";
import { AppDispatch } from "../../redux/store";
import { setFeatureFlags } from "../../redux/reducers/FeatureFlag";

interface AuthenticationWrapperProps {
  children: React.ReactNode;
  isSignedIn: boolean;
}

/**
 * AuthenticationWrapper handles authentication state management and initialization
 * after Amplify has been configured (which happens in index.tsx bootstrap).
 *
 * It:
 * - Manages user authentication via useAuthentication hook
 * - Fetches and sets feature flags once user is authenticated
 * - Provides a loading state while authentication is in progress
 * - Shows error notification if feature flag retrieval fails
 */
export function AuthenticationWrapper({
  children,
  isSignedIn,
}: AuthenticationWrapperProps) {
  const dispatch = useDispatch<AppDispatch>();
  const [isInitialized, setIsInitialized] = useState(false);
  const [initializationError, setInitializationError] = useState<string | null>(
    null,
  );

  // Initialize authentication - Amplify is already configured by bootstrap in index.tsx
  useAuthentication();

  // Initialize user state after authentication
  useEffect(() => {
    if (!isSignedIn || isInitialized) {
      return;
    }

    const initializeUserState = async () => {
      try {
        const userFeatureFlagState = await fetchFeatureFlags();
        dispatch(setFeatureFlags(userFeatureFlagState));
        setIsInitialized(true);
      } catch (e) {
        logger.error({
          loggerMsg: "Error initializing user state",
          e,
        });
        setInitializationError(
          "Failed to load application settings. Please try reloading the page.",
        );
      }
    };

    initializeUserState();
  }, [isSignedIn, isInitialized, dispatch]);

  // Show error state if initialization failed
  if (initializationError) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="max-w-md text-center p-6 bg-destructive/10 border border-destructive/20 rounded-lg">
          <div className="mb-4">
            <svg
              className="mx-auto h-12 w-12 text-destructive"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
              />
            </svg>
          </div>
          <h2 className="text-lg font-semibold mb-2 text-destructive">
            Initialization Error
          </h2>
          <p className="text-muted-foreground mb-4">{initializationError}</p>
          <button
            onClick={() => window.location.reload()}
            className="px-4 py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary/90 transition-colors"
          >
            Reload Page
          </button>
        </div>
      </div>
    );
  }

  // Show loading state while authentication is in progress
  if (!isSignedIn || !isInitialized) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4" />
          <p className="text-muted-foreground">Authenticating...</p>
        </div>
      </div>
    );
  }

  return <>{children}</>;
}
